
import { defineComponent, ref, computed } from "vue";
import { trans } from "@/globals/translator";
import { useIntersectionObserver } from "@vueuse/core";
import useArticles from "@/composables/useArticles";
import PageTitle from "@/components/common/PageTitle.vue";
import ArticleCard from "@/components/article/ArticleCard.vue";
import Grid from "@moonlight/vue3/components/Grid.vue";
import Row from "@moonlight/vue3/components/Row.vue";
import Col from "@moonlight/vue3/components/Col.vue";
import Loading from "@/components/common/Loading.vue";
import { useHead } from "@vueuse/head";

export default defineComponent({
  components: {
    PageTitle,
    Grid,
    Row,
    Col,
    ArticleCard,
    Loading,
  },
  setup() {
    const { articles, articlesWithMetadata, fetchArticles } = useArticles();

    const { endOfPageRef, hasNextPage, isFetchingNextPage } =
      useArticlesPaginator();

    function useArticlesPaginator() {
      const endOfPageRef = ref<HTMLDivElement>();

      const lastLoadedPage = computed(() => {
        let lastLoaded = 0;

        Object.values(articlesWithMetadata.value).forEach(
          (articleWithMetadata) => {
            if (
              articleWithMetadata.page > lastLoaded &&
              !articleWithMetadata.error &&
              !articleWithMetadata.pending
            ) {
              lastLoaded = articleWithMetadata.page;
            }
          }
        );

        return lastLoaded;
      });

      const hasNextPage = computed(() => {
        if (lastLoadedPage.value === 0) return true;

        const lastLoaded = articlesWithMetadata.value[lastLoadedPage.value];
        if (!lastLoaded) return true;

        return (
          articlesWithMetadata.value[lastLoadedPage.value].articles.length !== 0
        );
      });

      const isFetchingNextPage = computed(() => {
        return articlesWithMetadata.value[lastLoadedPage.value + 1]?.pending;
      });

      // Load next page when reaching it
      useIntersectionObserver(endOfPageRef, ([{ isIntersecting }]) => {
        if (isIntersecting && !isFetchingNextPage.value) {
          fetchArticles(lastLoadedPage.value + 1);
        }
      });

      return {
        endOfPageRef,
        hasNextPage,
        isFetchingNextPage,
      };
    }

    // initial load
    if (!articles.value.length) {
      fetchArticles();
    }

    useHead({
      title: computed(
        () =>
          trans("general.window_title_template", {
            current_page: trans("general.news"),
          }) as string
      ),
    });

    return {
      trans,
      articles,
      endOfPageRef,
      hasNextPage,
      isFetchingNextPage,
    };
  },
});
